import type { BadgeColors } from "@/components/base/badges/badge-types";

/**
 * Maps a backend iconColor string (e.g. "Blue", "Blue-Light", "Gray-Blue") to
 * the corresponding utility CSS custom properties from theme.css.
 *
 * The backend value maps to the --color-utility-{key}-600 / -100 scale:
 *   "Blue"         → --color-utility-blue-600 / -100
 *   "Blue-Light"   → --color-utility-blue-light-600 / -100
 *   "Blue-Dark"    → --color-utility-blue-dark-600 / -100
 *   "Gray"         → --color-utility-gray-600 / -100
 *   "Gray-Blue"    → --color-utility-gray-blue-600 / -100
 *   "Green"        → --color-utility-green-600 / -100
 *   "Orange"       → --color-utility-orange-600 / -100
 *   "Orange-Dark"  → --color-utility-orange-dark-600 / -100
 *   "Indigo"       → --color-utility-indigo-600 / -100
 *   "Fuchsia"      → --color-utility-fuchsia-600 / -100
 *   "Pink"         → --color-utility-pink-600 / -100
 *   "Purple"       → --color-utility-purple-600 / -100
 *   "Yellow"       → --color-utility-yellow-600 / -100
 *   "Brand"        → --color-utility-brand-600 / -100
 *   "Error"        → --color-utility-error-600 / -100
 *   "Warning"      → --color-utility-warning-600 / -100
 *   "Success"      → --color-utility-success-600 / -100
 */
export function resolveStatusCategoryColors(iconColor?: string | null): {
	/** Border and text color — -600 shade */
	color: string;
	/** Light background tint — -100 shade */
	bgColor: string;
} {
	const key = iconColor?.trim().toLowerCase().replace(/\s+/g, "-") || "gray";
	return {
		color: `var(--color-utility-${key}-600)`,
		bgColor: `var(--color-utility-${key}-100)`,
	};
}

/**
 * Maps a backend status-category `iconColor` to a Badge `color` prop.
 */
export function resolveStatusCategoryBadgeColor(
	iconColor?: string | null,
): BadgeColors {
	const key = iconColor?.trim().toLowerCase().replace(/\s+/g, "-") || "gray";

	switch (key) {
		case "brand":
			return "brand";
		case "error":
			return "error";
		case "warning":
		case "yellow":
			return "warning";
		case "success":
		case "green":
			return "success";
		case "gray-blue":
			return "gray-blue";
		case "blue-light":
			return "blue-light";
		case "blue":
		case "blue-dark":
			return "blue";
		case "indigo":
			return "indigo";
		case "purple":
			return "purple";
		case "pink":
		case "fuchsia":
			return "pink";
		case "orange":
		case "orange-dark":
			return "orange";
		case "gray":
		default:
			return "gray";
	}
}

/**
 * Returns colors for a status category using the backend iconColor value.
 * Falls back to resolveStatusCategoryColors for all categories.
 */
export function resolveStatusCategoryColorsWithOverrides(
	iconColor?: string | null,
): {
	color: string;
	bgColor: string;
} {
	return resolveStatusCategoryColors(iconColor);
}
